import React from 'react';

import * as Analytics from 'analytics/Analytics';
import {structuredTableActionOrigin} from 'analytics/Util';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import ValidationErrorsTable, {SpreadsheetType} from 'bulk-import/panes/ValidationErrorsTable';
import {SpreadsheetValidationErrorInfo} from 'bulk-import/SpreadsheetBulkImportConstants';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {callSampleStatusWarningModal} from 'inventory/sample-status/SampleStatusWarningModal';
import {stubbableFunctions} from 'notebook/integrations/samples/transfer-table-columns/InventoryTableQuantityCustomEval';
import {
  TransferStructuredTable,
  TransferStructuredTableAdapter,
  TransferStructuredTableMetadata,
  TransferStructuredTableRowMetadata,
} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import {ACTION_TRANSFER} from 'notebook/store/VersioningConstants';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import makeSimpleActionFunction, {
  getSubmitActionFunctionIcon,
  getSubmitButtonHidden,
  getSubmitButtonStyle,
} from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {
  abortAction,
  ActionResultType,
  batchedSubmissionGenerator,
  getSubmissionBatchSize,
  isBatchingRequired,
  performServerSideTableWriteActionWithAudits,
  ServerSideWriteActionResult,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import invokeStructuredTableMappings from 'notebook/structured-tables/structured-table-mappings/invokeStructuredTableMappings';
import {
  TableActionFunctionParameters,
  StructuredTable,
  StructuredTableRow,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import {confirmIfClearingSampleOwners} from 'notebook/structured-tables/util/getSampleControlRolesFromTable';
import {isInExecutableCanvas} from 'notebook/structured-tables/util/isInExecutableCanvas';
import isTableActionButtonDisabled from 'notebook/structured-tables/util/isTableActionButtonDisabled';
import rowHasUnsubmittedChanges from 'notebook/structured-tables/util/rowHasUnsubmittedChanges';
import tableActionButtonDisabledTooltip from 'notebook/structured-tables/util/tableActionButtonDisabledTooltip';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {TableIndicatorStatus} from 'notebook/tables/errors/TableErrorConstants';
import {EvaluatedCell, EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {formatTypedValue, getStorableInfoFromTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import notifications from 'notifications';
import {parseGridPosition} from 'samples/SampleUtil';
import * as StorableActions from 'samples/StorableActions';
import {PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import Modal from 'ui-library/modals/Modal';

import {StructuredTableSubmissionScope} from '../StructuredTableApiConstants';
import {
  getRowAwareLoadingMessage,
  RowSubmissionRange,
  getStructuredTableSubmissionScope,
} from './makeSubmitRowActionFunction';

interface TransferErrorsModalProps {
  errorData: SpreadsheetValidationErrorInfo;
  onRequestClose: () => void;
}

export function TransferErrorsModal(props: TransferErrorsModalProps): JSX.Element {
  return (
    <Modal
      enableRedesign={true}
      title="Errors when transferring"
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
        },
      ]}
    >
      <ValidationErrorsTable
        spreadsheetType={SpreadsheetType.STRUCTURED_TABLE}
        errorData={props.errorData}
        resolvableErrorStatuses={{}}
      />
    </Modal>
  );
}

async function attemptTransfer(
  entryId: string,
  structuredTable: StructuredTable<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  submissionScope?: StructuredTableSubmissionScope,
  latestVersionId?: number,
  dataModsHeader?: DataModificationsHeader
): Promise<ActionFunctionDAO.TransferSubmitResponse> {
  return await ActionFunctionDAO.transfer(
    entryId,
    structuredTable,
    computedTable,
    actionFunctionContext,
    selection,
    submissionScope,
    latestVersionId,
    dataModsHeader
  );
}

async function attemptBatchedTransfer(
  entryId: string,
  batchSize: number,
  structuredTable: TransferStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection: RowSubmissionRange | undefined,
  submissionScope: StructuredTableSubmissionScope,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.TransferSubmitResponse>> {
  let latestVersionId: number | undefined;
  const responseData: ActionFunctionDAO.TransferSubmitResponse = {
    destinationContainers: [],
    entryVersionId: -1,
  };
  let partialSuccess;
  let submittedRange: RowSubmissionRange = {
    start: selection ? selection.start : 0,
    end: selection ? selection.start : 0,
  };
  const batches = batchedSubmissionGenerator(computedTable, batchSize, structuredTable, selection);

  try {
    for (const {range, evaluatedRowBatch} of batches) {
      if (updateStatus) {
        updateStatus(
          getRowAwareLoadingMessage(range, 'Transferring contents for rows', 'Transferring content of row')
        );
      }
      const batchResult = await attemptTransfer(
        entryId,
        structuredTable,
        evaluatedRowBatch,
        actionFunctionContext,
        range,
        submissionScope,
        latestVersionId,
        dataModsHeader
      );
      latestVersionId = batchResult.entryVersionId;
      partialSuccess = true;
      submittedRange = {...submittedRange, end: range.end};
      responseData.destinationContainers.push(...batchResult.destinationContainers);
    }
  } catch (e) {
    // Show modal with error info if exception stemmed from the bulk adapter
    if (e.responseJSON && e.responseJSON.bulkAdapterErrorInfo) {
      await LoadingScreen.withLoadingScreenHidden(() =>
        legacyRunModal<TransferErrorsModalProps>(TransferErrorsModal, {
          errorData: e.responseJSON.bulkAdapterErrorInfo,
        })
      );
      if (partialSuccess) {
        return {resultType: ActionResultType.PARTIAL_SUCCESS, responseData, submittedRange};
      } else {
        return abortAction();
      }
    }
    if (partialSuccess) {
      notifications.error(notifications.getFriendlyError(e) ?? 'An unexpected error occured.');
      return {resultType: ActionResultType.PARTIAL_SUCCESS, responseData, submittedRange};
    }
    throw e;
  }
  return {resultType: ActionResultType.SUCCESS, responseData};
}

export async function doPerformTransfer(
  entryId: string,
  structuredTable: StructuredTable<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.TransferSubmitResponse>> {
  const submissionScope = getStructuredTableSubmissionScope(selection);
  const batchSize = getSubmissionBatchSize(StructuredTableKind.TRANSFER);
  if (isBatchingRequired(computedTable, batchSize, selection)) {
    return await attemptBatchedTransfer(
      entryId,
      batchSize!,
      structuredTable,
      computedTable,
      actionFunctionContext,
      selection,
      submissionScope,
      updateStatus,
      dataModsHeader
    );
  } else {
    const pertinentComputedTable = selection
      ? computedTable.slice(selection.start, selection.end)
      : computedTable;

    try {
      const transferResult = await attemptTransfer(
        entryId,
        structuredTable,
        pertinentComputedTable,
        actionFunctionContext,
        selection,
        submissionScope,
        undefined, // latestVersionId
        dataModsHeader
      );
      return {
        resultType: ActionResultType.SUCCESS,
        responseData: transferResult,
      };
    } catch (e) {
      if (e.responseJSON && e.responseJSON.bulkAdapterErrorInfo) {
        await LoadingScreen.withLoadingScreenHidden(() =>
          legacyRunModal<TransferErrorsModalProps>(TransferErrorsModal, {
            errorData: e.responseJSON.bulkAdapterErrorInfo,
          })
        );
        return abortAction();
      } else {
        throw e;
      }
    }
  }
}

export async function getSampleIdFromCell({
  structuredTable,
  slicedStructuredTable,
  computedTable,
  rowIndex,
  colIndex,
  sourcePositionColumnIndex,
}: {
  structuredTable: TransferStructuredTable;
  slicedStructuredTable?: Array<StructuredTableRow<TransferStructuredTableRowMetadata>>;
  computedTable: EvaluatedTable;
  rowIndex: number;
  colIndex: number;
  sourcePositionColumnIndex: number;
}): Promise<string | null> {
  /**
   * Helper function to return a sampleId that could have sample control warnings or null otherwise.
   */
  const typedValue =
    computedTable[rowIndex][colIndex].value ??
    (slicedStructuredTable
      ? slicedStructuredTable[rowIndex].cells[colIndex].typedValue
      : structuredTable.rows[rowIndex].cells[colIndex].typedValue);
  const storableInfo = typedValue ? getStorableInfoFromTypedValue(typedValue) : null;
  if (!storableInfo) {
    return null;
  }

  let sampleId: string | null = null;

  const {columnType, columnGroup} = structuredTable.columns[colIndex];

  if (columnType === TableColumnType.CONTAINERS) {
    // Any container should be included
    sampleId = storableInfo.id;
  } else if (columnType === TableColumnType.PLATE) {
    // Return the plate itself if a well plate, otherwise the container at the position in the plate
    const data = await stubbableFunctions.doPlateQuery(storableInfo.id);
    const plate = data.plates[0];
    assert(!isInaccessible(plate), 'Uncaught inaccessible plate pre-transfer');
    assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');
    if (plate.storableSchema.plateSchemaType === PlateSchemaType.FIXED_PLATE) {
      sampleId = storableInfo.id;
    } else if (columnGroup === TransferTableColumnGroup.SOURCE) {
      // Matrix plates can only be sources
      const positionTypedValue =
        computedTable[rowIndex][sourcePositionColumnIndex]?.value ??
        (slicedStructuredTable
          ? slicedStructuredTable[rowIndex].cells[sourcePositionColumnIndex].typedValue
          : structuredTable.rows[rowIndex].cells[sourcePositionColumnIndex].typedValue);
      const {rowIndex: rowIndexPosition, columnIndex: columnIndexPosition} = parseGridPosition({
        position: formatTypedValue(positionTypedValue),
        gridHeight: plate.storableSchema.height,
        gridWidth: plate.storableSchema.width,
      });
      const plateWell = plate.cells[rowIndexPosition][columnIndexPosition]?.container;
      assert(!isInaccessible(plateWell), 'Uncaught inaccessible plate well pre-transfer');
      sampleId = plateWell?.id ?? null;
    }
  }
  return sampleId;
}

export async function getSampleIdsForStatusWarningFromStructuredTable({
  structuredTable,
  slicedStructuredTable,
  computedTable,
}: {
  structuredTable: TransferStructuredTable;
  slicedStructuredTable?: Array<StructuredTableRow<TransferStructuredTableRowMetadata>>;
  computedTable: EvaluatedTable;
}): Promise<Array<string>> {
  const {sourcePositionColumnIndex} =
    TransferStructuredTableAdapter.findSourcePrimarySampleColumnIndices(structuredTable);

  const ids = await Promise.all(
    computedTable.map(async (row, rowIndex) => {
      const storableIds: Array<string> = [];
      if (
        rowHasUnsubmittedChanges(
          row,
          slicedStructuredTable ? slicedStructuredTable[rowIndex] : structuredTable.rows[rowIndex],
          structuredTable.columns,
          structuredTable.schemas
        )
      ) {
        for (const [colIndex] of structuredTable.columns.entries()) {
          const sampleId = await getSampleIdFromCell({
            structuredTable,
            slicedStructuredTable,
            computedTable,
            rowIndex,
            colIndex,
            sourcePositionColumnIndex,
          });
          if (sampleId != null) {
            storableIds.push(sampleId);
          }
        }
      }
      return storableIds;
    })
  );
  return ids.flat();
}

export async function onInvokeTransferActionFunction(
  actionFunctionParams: TableActionFunctionParameters<TransferStructuredTableMetadata, {}>
): Promise<void> {
  const {
    entryOrTemplateId,
    structuredTable,
    structuredTableAdapter,
    computedTable,
    actionFunctionContext,
    table,
  } = actionFunctionParams;
  assert(
    isInExecutableCanvas(actionFunctionParams),
    'Transfer action is only supported in entries and executable documents.',
    {apiId: entryOrTemplateId}
  );
  assert(entryOrTemplateId != null, 'Document ID not provided');
  const sampleIds = await getSampleIdsForStatusWarningFromStructuredTable({structuredTable, computedTable});
  const {shouldProceed: proceedWithSampleOverride} = await callSampleStatusWarningModal({
    sampleIds: [...new Set(sampleIds)],
    trackingParams: {
      actionOrigin: structuredTableActionOrigin(structuredTable),
      actionAttempted: 'transfer',
      eventGroupId: Analytics.makeAnalyticsEventId(),
    },
    canSelectMultiple: false,
    showOnlyWarnings: true,
  });

  const proceedWithSubmit =
    proceedWithSampleOverride &&
    (await confirmIfClearingSampleOwners({
      structuredTable,
      computedTable,
      targetSampleColumnGroup: TransferTableColumnGroup.TARGET,
    }));

  if (proceedWithSubmit) {
    await performServerSideTableWriteActionWithAudits<ActionFunctionDAO.TransferSubmitResponse>({
      entryId: entryOrTemplateId,
      loadingScreenMessage: 'Transferring...',
      autoSaverAction: ACTION_TRANSFER,
      doPerformAction: ({updateStatus, dataModsHeader}) =>
        doPerformTransfer(
          entryOrTemplateId,
          structuredTable,
          computedTable,
          actionFunctionContext,
          undefined, // selection
          updateStatus,
          dataModsHeader
        ),
      doPostRefresh: (actionResult) => {
        StorableActions.dispatchStorablesLoaded(actionResult.responseData.destinationContainers);
        invokeStructuredTableMappings(
          entryOrTemplateId,
          structuredTable,
          // on a partial success we want to map only the rows that were successfully submitted
          actionResult.resultType === ActionResultType.PARTIAL_SUCCESS
            ? actionResult.submittedRange
            : undefined
        );
        return Promise.resolve();
      },
      analyticsActionForPerfTracking: ELN_ANALYTICS_ACTIONS.TRANSFERRED_SAMPLES_FROM_STRUCTURED_TABLE.action,
      structuredTables: [structuredTable],
      structuredTableAdapters: [structuredTableAdapter],
      computedTables: [computedTable],
      tables: table ? [table] : [],
    });
  }
}

export const transferActionFunction = makeSimpleActionFunction<TransferStructuredTableMetadata, {}>({
  label: (params) => {
    if (
      params.tableStatus === TableIndicatorStatus.NO_STATUS ||
      params.tableStatus === TableIndicatorStatus.SUBMITTED_BYPASSING_VALIDATION
    ) {
      return 'Transferred';
    }
    return 'Transfer';
  },
  icon: getSubmitActionFunctionIcon(),
  buttonDesign: {
    buttonStyle: (params) => getSubmitButtonStyle(params.tableStatus, ButtonStyle.SECONDARY),
    showTextOnButton: true,
  },
  onInvoke: onInvokeTransferActionFunction,
  getDisabled: (params) => isTableActionButtonDisabled(params),
  getTooltip: tableActionButtonDisabledTooltip,
  getHidden: (params) => getSubmitButtonHidden(params.tableStatus, params.isCollapsed),
});
